import { useEffect, useState, useRef, ChangeEvent } from 'react';
import cover from '../../assets/filter-cover.png';
import Search from '../../assets/icons/search.svg?react';
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { ButtonGroup } from "../../components/ui/ButtonGroup";
import { Carousel } from "../../components/Carousel";
import { VideoVibes } from '../../components/VideoVibes';
import VideoViewer from './VideoViewer';
import { useMedia } from '../../hooks/useMedia';
import { useGenre } from '../../hooks/useGenre';
import { Media } from 'src/types/media';
import { useSearchParams, useNavigate } from 'react-router';
import { useWindowSize } from '../../hooks/useWindowSize';
import { useStation } from '../../contexts/StationContext';
import AdComponent from '../../components/AdComponent';
import ScrollArrows from "../../components/ScrollArrows";
import { generateDetailUrl, generateUrlName, getIdFromUrlName } from "../../utils/urlUtils";
import SeoMeta from "../../components/Seo-Meta";
import { useMaster } from '../../hooks/useMaster';
const Video = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [id,setId] = useState("")
    const [pageSize, setPageSize] = useState(6);
    const [isVideoViewerVisible, setIsVideoViewerVisible] = useState(false);
    const { mediaList: videoList, media } = useMedia('Video', 1, 6, false, id ?? undefined);
    const { mediaList: videoListByEditorChoice } = useMedia('Video', 1, 6, true);
    const { mediaList: videoListByFilter, mediaTotal, setGenre, setSearchQuery } = useMedia('Video', 1, pageSize);
    const { genres } = useGenre();
    const { width } = useWindowSize();
    const videoPlayerRef = useRef<HTMLIFrameElement>(null);
    const { station } = useStation();
    const recommendedListRef = useRef<HTMLDivElement>(null);
    const editorChoiceListRef = useRef<HTMLDivElement>(null);
    const { metadata } = useMaster();
    useEffect(() => {
        const urlName = searchParams.get('id')
        const id = getIdFromUrlName(urlName||"") 
        setId(id)
        setIsVideoViewerVisible(!!id);
    }, [searchParams]);

    const handleFilterByGenre = (selected: { id: number, name: string }): void => {
        setGenre(selected.name === 'All' ? 0 : selected.id);
    }

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }

    const onPlay = (media: Media) => {
        navigate(`?id=${generateUrlName(media.title, media.id) }`);
        setIsVideoViewerVisible(true);
        videoPlayerRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }

    return (
        <>
            <SeoMeta
                title ={`${metadata?.videoMetaTitle || 'Videos'} | Red FM ${station?.name || ''}`} 
                description={metadata?.videoMetaDescription || `Explore a wide range of videos from Red FM ${station?.name || ''}. Discover videos, podcasts, and more to enjoy on the go.`}
                keywords={metadata?.videoMetaKeywords || `Videos, Podcasts, Red FM, ${station?.name || ''}, Red FM ${station?.name || ''}`}
                url={generateDetailUrl(station?.name || '', station.id || '', 'video')}
            />
            {!isVideoViewerVisible && <Carousel slideDelay={3000} animationSpeed={500} />}
            {isVideoViewerVisible && <div ref={videoPlayerRef} className="scroll-m-28"><VideoViewer media={media} /></div>}
            {!!videoList?.length &&
                <section className="flex flex-col gap-4 md:gap-6 py-6 md:py-8 lg:py-10 bg-[#F2F4F7]">
                    <div className="flex justify-between items-center px-4 md:px-6 z-10">
                        <h2 className="text-lg md:text-2xl font-bold font-kanit">Trending</h2>
                    </div>
                    <div ref={recommendedListRef} className="flex overflow-x-auto gap-4 md:gap-6 [&::-webkit-scrollbar]:hidden -my-4 py-4 px-6 scroll-smooth">
                        {
                            videoList.map((video, index) => (
                                <VideoVibes
                                    key={index}
                                    title={video.title}
                                    subtitle={video.description}
                                    videoUrl={video.mediaUrl}
                                    className="w-[300px] sm:w-[350px] lg:w-[400px]"
                                    onClick={() => onPlay(video)}
                                />
                            ))
                        }
                    </div>
                    <div className="px-4 md:px-10 lg:px-20 z-10  relative flex justify-end">  
                        <ScrollArrows reference={recommendedListRef} />
                    </div>
                </section>
            }
            {!!videoListByEditorChoice?.length && (
                <section className="flex flex-col gap-4 md:gap-6 py-6 md:py-8 lg:py-10">
                    <div className="flex justify-between items-center px-4 md:px-6 z-10">
                        <h2 className="text-lg md:text-2xl font-bold font-kanit">Editor Choice</h2>
                    </div>
                    <div ref={editorChoiceListRef} className="flex overflow-x-auto gap-4 md:gap-6 [&::-webkit-scrollbar]:hidden -my-4 py-4 px-6 scroll-smooth">
                        {
                            videoListByEditorChoice.map((video, index) => (
                                <VideoVibes
                                    key={index}
                                    title={video.title}
                                    subtitle={video.description}
                                    thumbnailUrl={video.images[0]}
                                    videoUrl={video.mediaUrl}
                                    className="w-[300px] sm:w-[350px] lg:w-[400px]"
                                    onClick={() => onPlay(video)}
                                />
                            ))
                        }
                    </div>
                    <div className="px-4 md:px-10 lg:px-20 z-10  relative flex justify-end">  
                        <ScrollArrows reference={editorChoiceListRef} />
                    </div>
                </section>
            )}
            <div className="flex justify-center items-center w-full">
            <AdComponent className="flex justify-center items-center w-[320px] md:w-[768px] h-[50px] md:h-[90px] z-10 text-gray-500 bg-[#F2F4F7]" channel={`video-list-horizontal-1-${station.name}`} />
            </div>
            <section className="flex flex-col md:justify-center items-center gap-6 px-4 py-6 w-full bg-cover bg-center" style={{ backgroundImage: `linear-gradient(#FFFFFF00, #FFFFFF), url(${cover})` }}>
                <h2 className="hidden md:block text-lg md:text-2xl font-bold font-kanit text-[#D0D5DD]">All Videos</h2>
                <Input
                    className="w-full md:w-[750px]"
                    placeholder="Search"
                    size={width >= 1024 ? "lg" : "md"}
                    iconPosition="leading"
                    icon={<Search className="text-gray-500" />}
                    onChange={handleSearch}
                />
                {videoListByFilter?.length === 0 && (
                    <h2 className="text-lg md:text-2xl font-bold font-kanit text-center mb-4 text-gray-500">No videos found!</h2>
                )}
                {videoListByFilter?.length > 0 && (
                    <> 
                <p className="md:text-lg">Explore playlists, Video podcasts, and other audio gems from the RedFM</p>
                <ButtonGroup
                    options={genres}
                    variant="primary"
                    onChange={handleFilterByGenre}
                    className="justify-start sm:justify-center"
                    size="md"
                    disabled={false}
                    backgroundColor="#1B1B1B"
                    textColor="#FFFFFF"
                    rounded="md"
                />
                </>
                )}
                </section>
            <section className="flex flex-col gap-4 md:gap-6 pb-6 md:py-12">
                {videoListByFilter?.length > 0 && (
                    <>
                        {/* <div className="flex overflow-x-auto md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 [&::-webkit-scrollbar]:hidden px-4 md:px-10"> */}
                        <div className="grid sm:grid-cols-[repeat(2,minmax(0,350px))] md:grid-cols-[repeat(2,350px)] lg:grid-cols-[repeat(3,minmax(0,400px))] justify-center gap-4 md:gap-6 px-4 md:px-10">
                            {
                                videoListByFilter.map((video, index) => (
                                    <VideoVibes
                                        key={index}
                                        title={video.title}
                                        subtitle={video.description}
                                        duration={video.duration}
                                        thumbnailUrl={video.images[0]}
                                        videoUrl={video.mediaUrl}
                                        onClick={() => onPlay(video)}
                                    />
                                ))
                            }
                        </div>
                        {videoListByFilter?.length < mediaTotal &&
                            <span className="flex justify-center items-center">
                                <Button variant="primary" onClick={() => setPageSize(pageSize + 6)}>View More Videos</Button>
                            </span>
                        }
                    </>
                )}
              
            </section>
        </>
    );
};

export default Video;